export const DEFAULT_SPATIAL_INDEX_CELL_SIZE = 512;

const MAX_GRID_CELLS_PER_RECT = 4096;

function finite(value) {
  return Number.isFinite(value) ? value : null;
}

function rectBounds(rect) {
  if (!rect) return null;
  const x = finite(rect.x);
  const y = finite(rect.y);
  const width = finite(rect.width);
  const height = finite(rect.height);
  if (x !== null && y !== null && width !== null && height !== null) {
    return {
      minX: Math.min(x, x + width),
      minY: Math.min(y, y + height),
      maxX: Math.max(x, x + width),
      maxY: Math.max(y, y + height),
    };
  }

  const minX = finite(rect.minX);
  const minY = finite(rect.minY);
  const maxX = finite(rect.maxX);
  const maxY = finite(rect.maxY);
  if (minX === null || minY === null || maxX === null || maxY === null) return null;
  return {
    minX: Math.min(minX, maxX),
    minY: Math.min(minY, maxY),
    maxX: Math.max(minX, maxX),
    maxY: Math.max(minY, maxY),
  };
}

function intersects(left, right) {
  return !(
    left.maxX < right.minX
    || left.minX > right.maxX
    || left.maxY < right.minY
    || left.minY > right.maxY
  );
}

function cellRange(bounds, cellSize) {
  const minCellX = Math.floor(bounds.minX / cellSize);
  const minCellY = Math.floor(bounds.minY / cellSize);
  const maxCellX = Math.floor(bounds.maxX / cellSize);
  const maxCellY = Math.floor(bounds.maxY / cellSize);
  return {
    minCellX,
    minCellY,
    maxCellX,
    maxCellY,
    cellCount: (maxCellX - minCellX + 1) * (maxCellY - minCellY + 1),
  };
}

function cellKey(x, y) {
  return `${x}:${y}`;
}

/**
 * Build an immutable broad-phase index for map-space rectangles. Queries are
 * returned in source order so replacing a linear scan cannot change layout or
 * collision tie-breaking.
 */
export function createSpatialIndex(items = [], {
  getRect = (item) => item?.rect || item,
  cellSize = DEFAULT_SPATIAL_INDEX_CELL_SIZE,
} = {}) {
  const safeItems = Array.isArray(items) ? items : [];
  const safeCellSize = Number.isFinite(cellSize) && cellSize > 0
    ? cellSize
    : DEFAULT_SPATIAL_INDEX_CELL_SIZE;
  const entries = [];
  const buckets = new Map();
  const overflowEntryIndexes = [];

  safeItems.forEach((item) => {
    const bounds = rectBounds(getRect(item));
    if (!bounds) return;
    const entryIndex = entries.length;
    entries.push({ item, bounds });
    const range = cellRange(bounds, safeCellSize);
    if (range.cellCount > MAX_GRID_CELLS_PER_RECT) {
      overflowEntryIndexes.push(entryIndex);
      return;
    }
    for (let x = range.minCellX; x <= range.maxCellX; x += 1) {
      for (let y = range.minCellY; y <= range.maxCellY; y += 1) {
        const key = cellKey(x, y);
        const bucket = buckets.get(key);
        if (bucket) bucket.push(entryIndex);
        else buckets.set(key, [entryIndex]);
      }
    }
  });

  const query = (rect, { padding = 0 } = {}) => {
    const bounds = rectBounds(rect);
    if (!bounds) return [];
    const safePadding = Math.max(0, Number.isFinite(padding) ? padding : 0);
    const queryBounds = {
      minX: bounds.minX - safePadding,
      minY: bounds.minY - safePadding,
      maxX: bounds.maxX + safePadding,
      maxY: bounds.maxY + safePadding,
    };
    const range = cellRange(queryBounds, safeCellSize);
    let candidateIndexes;
    if (range.cellCount > MAX_GRID_CELLS_PER_RECT) {
      candidateIndexes = entries.map((_, index) => index);
    } else {
      const candidates = new Set(overflowEntryIndexes);
      for (let x = range.minCellX; x <= range.maxCellX; x += 1) {
        for (let y = range.minCellY; y <= range.maxCellY; y += 1) {
          for (const entryIndex of buckets.get(cellKey(x, y)) || []) {
            candidates.add(entryIndex);
          }
        }
      }
      candidateIndexes = [...candidates].sort((left, right) => left - right);
    }

    return candidateIndexes
      .filter((entryIndex) => intersects(entries[entryIndex].bounds, queryBounds))
      .map((entryIndex) => entries[entryIndex].item);
  };

  return Object.freeze({
    size: entries.length,
    cellSize: safeCellSize,
    query,
  });
}
